import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { QueryResult, useApolloClient } from 'react-apollo';
import { Chip, Grid, Box, Typography } from '@material-ui/core';
import { withGroupModerators } from 'providers/queries';
import {
  Autocomplete,
  AutocompleteGetTagProps,
  AutocompleteRenderInputParams,
} from '@material-ui/lab';
import { useParams } from 'react-router';
import { includes } from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { groupManagers } from 'apollo/reducers';
import { useCurrentUser, useGroupData, useGroupModerators, useNotify } from 'hooks';
import {
  CreateGroupModeratorMutation,
  DeleteGroupModeratorMutation,
  ProfileSuggestion,
  SuggestIndex,
  useCreateGroupModeratorMutation,
  useDeleteGroupModeratorMutation,
  UserSuggestDocument,
} from 'api';
import { createGroupModeratorResponse, deleteGroupModeratorResponse } from 'apollo/responses';
import { Loaders, UserAvatar } from 'components';
import useAutoCompleteStyles from 'components/Pickers/styles/useAutoCompleteStyles';
import { PickerInput, PopupIcon } from 'components/Pickers/components';

import { getGroupHexColor, Managers, moderatorIsOwner } from '../helpers';
import { IGroupForm } from '../types';

const useStyles = makeStyles(theme => ({
  chip: {
    borderRadius: 4,
  },
  deleteIcon: {
    width: 'inherit',
    lineHeight: 1.5,
  },
  owner: {
    cursor: 'default',
  },
}));

const Moderators: FC = () => {
  const classes = useStyles();
  const autoCompleteClasses = useAutoCompleteStyles();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const notify = useNotify();
  const currentUser = useCurrentUser();
  const { group_id } = useParams();
  const { groupModerators } = useGroupModerators();
  const { groupColors } = useGroupData();
  const { values } = useFormikContext<IGroupForm>();

  const hexColor = getGroupHexColor(groupColors, values.color);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [deletedManager, setDeletedManager] = useState(null);

  const [
    createGroupModeratorMutation,
    createGroupModeratorOptions,
  ] = useCreateGroupModeratorMutation({
    onError: notify.mutationError,
    onCompleted: handleAddManagerCompleted,
  });

  const [
    deleteGroupModeratorMutation,
    deleteGroupModeratorOptions,
  ] = useDeleteGroupModeratorMutation({
    onError: notify.mutationError,
    onCompleted: handleDeleteManagerCompleted,
  });

  useEffect(() => {
    setLoading(createGroupModeratorOptions.loading || deleteGroupModeratorOptions.loading);
  }, [createGroupModeratorOptions.loading, deleteGroupModeratorOptions.loading]);

  function handleAddManagerCompleted(mutation: CreateGroupModeratorMutation) {
    const message = t('groups:addManagerSuccess', {
      name: mutation.createGroupModerator.user.full_name,
      group: values.title,
    });
    notify.info({ message });
  }

  function handleDeleteManagerCompleted(mutation: DeleteGroupModeratorMutation) {
    const message = t('groups:deleteManagerSuccess', {
      name: deletedManager.user.full_name,
      group: values.title,
    });
    notify.info({ message });
  }

  function toggleOpen() {
    setOpen(!open);
  }

  function getModeratorIds() {
    return groupModerators.map(({ user_id }) => user_id);
  }

  function isOwner(moderator: Managers) {
    return moderatorIsOwner(moderator);
  }

  function handleChange(
    event: ChangeEvent<HTMLInputElement>,
    newSelectedOptions: ProfileSuggestion[],
  ) {
    setInputValue('');

    if (groupModerators.length < newSelectedOptions.length) {
      const moderator = newSelectedOptions[newSelectedOptions.length - 1];

      return createGroupModeratorMutation({
        variables: {
          group_id,
          user_id: moderator.id,
        },
        update: (cache, result) => {
          groupManagers({
            name: 'addManager',
            cache,
            result,
            variables: { group_id, targetUserId: moderator.id },
          });
        },
        // @ts-ignore
        optimisticResponse: createGroupModeratorResponse({
          groupId: group_id,
          moderator,
          currentUser,
        }),
      });
    }

    return null;
  }

  async function handleAsyncInputChange(nextInputValue: string) {
    setInputValue(nextInputValue);

    if (!nextInputValue) {
      return setOptions([]);
    }

    setLoading(true);
    const { data }: { data: QueryResult['data'] } = await apolloClient.query({
      query: UserSuggestDocument,
      variables: { prefix: inputValue, index: SuggestIndex.Suggestedprofiles },
    });

    setLoading(false);

    if (!data?.suggest) {
      return setOptions([]);
    }

    const { results } = data.suggest;
    setOptions(results.filter(({ id }: { id: string }) => !includes(getModeratorIds(), id)));
  }

  const handleRemove = (moderator: Managers) => (event: any) => {
    if (isOwner(moderator)) {
      return null;
    }

    setDeletedManager(moderator);
    return deleteGroupModeratorMutation({
      variables: {
        user_id: moderator.user_id,
        group_id,
      },
      update: (cache, result) => {
        groupManagers({
          name: 'removeManager',
          cache,
          result,
          variables: { group_id, targetUserId: moderator.user_id },
        });
      },
      optimisticResponse: deleteGroupModeratorResponse(),
    });
  };

  function renderTags(value: Managers[], getTagProps: AutocompleteGetTagProps) {
    return value.map((moderator: Managers, index: number) => {
      const { user } = moderator;
      const { id, full_name } = user;
      const isUserOwner = isOwner(moderator);

      return (
        <Chip
          className={classes.chip}
          classes={{ deleteIcon: classNames(classes.deleteIcon, { [classes.owner]: isUserOwner }) }}
          avatar={<UserAvatar size={24} user={user} />}
          key={id}
          label={full_name ? full_name : <Loaders.Standard height={15} />}
          deleteIcon={
            isUserOwner ? <Typography>{t('groups:roleOwner')}</Typography> : <CloseIcon />
          }
          {...getTagProps({ index })}
          onDelete={handleRemove(moderator)}
        />
      );
    });
  }

  function renderOption(option: ProfileSuggestion) {
    return (
      <Grid container alignItems="center">
        <Grid item>
          <Box mr={1}>
            <UserAvatar link size={30} user={option} />
          </Box>
        </Grid>
        <Grid item xs>
          {option.title}
        </Grid>
      </Grid>
    );
  }

  function renderInput(params: AutocompleteRenderInputParams) {
    return (
      <PickerInput
        working={loading}
        placeholder="groups:addManagers"
        onChange={handleAsyncInputChange}
        params={params}
      />
    );
  }

  return (
    <Autocomplete
      multiple
      fullWidth
      disableClearable
      disableCloseOnSelect
      classes={autoCompleteClasses}
      open={open}
      onOpen={toggleOpen}
      onClose={toggleOpen}
      getOptionLabel={({ title }: IGroupForm) => title}
      value={groupModerators}
      inputValue={inputValue}
      popupIcon={<PopupIcon open={open} color={hexColor} />}
      loading={loading}
      onChange={handleChange}
      renderTags={renderTags}
      renderInput={renderInput}
      renderOption={renderOption}
      options={options}
    />
  );
};

export default withGroupModerators(Moderators);
